import { DateTime } from 'luxon'

export interface ParseDateRangeOptions {
  now: string
}

interface DateRangeParts {
  baseDate?: string|undefined
  /** Offsets (`+1d`) and roundings (`/w`), applied left to right. */
  steps: DateMathStep[]
}

type DateMathStep =
  | { kind: 'add', amount: number, unit: string }
  | { kind: 'round', unit: string }

// A date, optionally with a time (seconds and fractions optional), an offset
// (`Z`, `+02:00`, `-0500`) and an IANA zone (`[Europe/Berlin]`, RFC 9557).
// Fractions matter: `toISO()` writes milliseconds.
const regexpIsoDateOrIsoDateWithOffset = `(?:\\d{4}-\\d{2}-\\d{2}(?:T\\d{2}:\\d{2}(?::\\d{2}(?:\\.\\d+)?)?(?:Z|[+-]\\d{2}:?\\d{2})?(?:\\[!?[A-Za-z0-9_+\\-/]+\\])?)?)`
const regexpNowOrDate = `(now|NOW|${regexpIsoDateOrIsoDateWithOffset})`
// One step of date math: an offset (`+1d`, `-2M`) or a rounding down to the
// start of the day, (ISO) week, month or year (`/d`, `/w`, `/M`, `/y`).
const regexpDateMathStep = `(?:[+-][0-9]+[yMwdhHms]|\\/[dwMy])`

const regexDateBase = new RegExp(regexpNowOrDate)
const regexDateMathStep = new RegExp(`^(?:([+-])([0-9]+)([yMwdhHms])|\\/([dwMy]))`)
const regexRelativeDate = new RegExp(`^(?:now|NOW)${regexpDateMathStep}*$`)

/**
 * Whole-value date math relative to the current time, such as `now`, `NOW-1d`,
 * `now/d`, `now-1M/M` or `now/w+6d` (steps apply left to right).
 */
export function isRelativeDate(value: unknown): value is string {
  return typeof value === 'string' && regexRelativeDate.test(value)
}

export function isIsoDate(text: string) {
  return text.match(regexpIsoDateOrIsoDateWithOffset)
}

export function parseDateRange(dateRange: string, { now }: ParseDateRangeOptions): DateTime {
  if (dateRange === 'now' || dateRange === 'NOW') {
    return DateTime.fromISO(now)
  }

  if (dateRange.startsWith('*')) {
    throw new Error('Wildcard needs to be handled outside')
  }

  const dateRangeParts = parseDateRangeString(dateRange)

  if (!dateRangeParts) {
    return null
  }

  let date: DateTime = getBaseDate(dateRangeParts, now)
  const roundingZone = getRoundingZone(dateRangeParts, now, date)

  for (const step of dateRangeParts.steps) {
    if (step.kind === 'round') {
      date = date.setZone(roundingZone).startOf(roundDownUnits[step.unit])
    } else if (step.amount) {
      date = modifyDate(step.unit, date, step.amount)
    }
  }

  return date
}

function parseDateRangeString(dateRange: string): DateRangeParts {
  if (!dateRange) {
    return null
  }

  const base = dateRange.match(regexDateBase)

  if (!base) {
    return null
  }

  const steps: DateMathStep[] = []
  let rest = dateRange.slice(base.index + base[0].length)
  let step = rest.match(regexDateMathStep)

  while (step) {
    const [text, sign, amount, unit, roundUnit] = step
    steps.push(roundUnit
      ? { kind: 'round', unit: roundUnit }
      : { kind: 'add', amount: Number(sign + amount), unit })
    rest = rest.slice(text.length)
    step = rest.match(regexDateMathStep)
  }

  return { baseDate: base[1], steps }
}

function getBaseDate(date: DateRangeParts, now: string): DateTime {
  const nowAsDateTime = DateTime.fromISO(now, {
    setZone: true // use timezone provided in string
  })

  if (date.baseDate === 'now' || date.baseDate === 'NOW') {
    return nowAsDateTime
  }
  
  return DateTime.fromISO(date.baseDate, {
    setZone: true, // use timezone provided in string, if any
    zone: nowAsDateTime.zone // fallback to timezone provided in now
  })
}

// `now` carries a fixed offset (e.g. +02:00, see @aeppic/shared/now). When
// that is the local offset, `now` and dates without their own offset round in
// the local zone, so week, month and year starts follow daylight saving time.
// Dates with their own offset round in it, and dates with a zone annotation
// (`…+01:00[Europe/Berlin]`) round in that zone.
function getRoundingZone(date: DateRangeParts, now: string, value: DateTime) {
  const nowAsDateTime = DateTime.fromISO(now, { setZone: true })
  const nowIsLocal = nowAsDateTime.setZone('local').offset === nowAsDateTime.offset
  const ownOffset = !/^now$/i.test(date.baseDate) && /(?:Z|[+-]\d{2}:?\d{2}|\])$/.test(date.baseDate)
  return nowIsLocal && !ownOffset ? value.setZone('local').zone : value.zone
}

const roundDownUnits = {
  d: 'day',
  w: 'week',
  M: 'month',
  y: 'year',
} as const

function modifyDate(operandType, date, valueToAdd) {
  const modifierFn = dateModifierByType[operandType]

  if (!modifierFn) {
    // tslint:disable-next-line:no-console
    console.warn(`Unknown operandType "${operandType}"`)
    return
  }

  return modifierFn(date, valueToAdd)
}

const dateModifierByType = {
  y(date, valueToAdd) {
    return date.plus({ years: valueToAdd })
  },
  M(date, valueToAdd) {
    return date.plus({ month: valueToAdd })
  },
  w(date, valueToAdd) {
    return date.plus({ weeks: valueToAdd })
  },
  d(date, valueToAdd) {
    return date.plus({ days: valueToAdd })
  },
  H(date, valueToAdd) {
    return dateModifierByType.h(date, valueToAdd)
  },
  h(date, valueToAdd) {
    return date.plus({ hours: valueToAdd })
  },
  m(date, valueToAdd) {
    return date.plus({ minutes: valueToAdd })
  },
  s(date, valueToAdd) {
    return date.plus({ seconds: valueToAdd })
  },
}
